import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  Users, Phone, Mail, Loader2, Check, Trash2, Target, UserCheck, UserX,
  UserPlus, Linkedin,
} from "lucide-react";

/**
 * DecisionMakersCard — multi-contact rotation for a lead.
 *
 * Renders the lead's `contacts[]` as a sortable list (highest decision-maker
 * score first), with inline actions:
 *   • "Find with Apollo"  → enrich with up to 5 ranked candidates (rate-limited).
 *   • "Add manual"        → tiny inline form (phone OR email required).
 *   • per-row: Activate / Confirm / Mark wrong person / LinkedIn / Delete.
 *
 * The currently-active contact (the phone the cadence engine actually dials)
 * is highlighted with a green dot + light blue background. Score badge color
 * encodes confidence (green ≥ 70, amber 40-69, zinc < 40).
 */

const STATUS_STYLES = {
  pending:      { label: "Pending",       cls: "bg-zinc-100 text-zinc-700" },
  tried:        { label: "Tried",         cls: "bg-amber-100 text-amber-800" },
  confirmed:    { label: "Confirmed",     cls: "bg-emerald-100 text-emerald-800" },
  wrong_person: { label: "Wrong person",  cls: "bg-rose-100 text-rose-800" },
  bad_number:   { label: "Bad number",    cls: "bg-rose-100 text-rose-800" },
  unsubscribed: { label: "Unsubscribed",  cls: "bg-red-100 text-red-800" },
};

const scoreColor = (s) => {
  const n = Number(s) || 0;
  if (n >= 70) return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (n >= 40) return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-zinc-100 text-zinc-600 border-zinc-200";
};

// Sort: active first; then highest score; then pending status priority.
const sortContacts = (contacts, activeId) =>
  [...(contacts || [])].sort((a, b) => {
    if (a.id === activeId) return -1;
    if (b.id === activeId) return 1;
    return (Number(b.score) || 0) - (Number(a.score) || 0);
  });

export default function DecisionMakersCard({ lead, onChange }) {
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", title: "", phone: "", email: "" });
  const contacts = sortContacts(lead?.contacts || [], lead?.active_contact_id);
  const activeId = lead?.active_contact_id;

  const findDecisionMakers = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/decision-makers/search`, {});
      toast.success(
        data.added > 0
          ? `Apollo agregó ${data.added} tomador(es) de decisión`
          : (data.message || "No se encontraron tomadores de decisión"),
      );
      onChange?.();
    } catch (e) {
      // 429 → cooldown: show the friendly Retry-After message.
      if (e?.response?.status === 429) {
        toast.error(e.response.data?.detail || "Espera unos segundos antes de volver a buscar.");
      } else {
        toast.error(formatApiError(e?.response?.data?.detail) || "Error al buscar tomadores de decisión");
      }
    } finally {
      setBusy(false);
    }
  };

  const addManual = async () => {
    if (!draft.phone && !draft.email) {
      toast.error("Phone or email is required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/contacts`, draft);
      toast.success("Contacto agregado");
      setDraft({ name: "", title: "", phone: "", email: "" });
      setAdding(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al agregar contacto");
    } finally {
      setBusy(false);
    }
  };

  const patchContact = async (cid, patch) => {
    setBusy(true);
    try {
      await api.patch(`/leads/${lead.id}/contacts/${cid}`, patch);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const removeContact = async (cid) => {
    if (!window.confirm("¿Eliminar este contacto?")) return;
    setBusy(true);
    try {
      await api.delete(`/leads/${lead.id}/contacts/${cid}`);
      toast.success("Contacto eliminado");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const activateContact = async (cid) => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/contacts/${cid}/activate`, {});
      toast.success("Contacto activo actualizado");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Activate failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-8 border border-zinc-200 bg-white" data-testid="decision-makers-section">
      <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#002FA7]" />
          <h3 className="font-heading text-xs font-bold uppercase tracking-widest text-zinc-900">
            Decision Makers
          </h3>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">
            ({contacts.length})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={findDecisionMakers}
            disabled={busy}
            data-testid="btn-find-decision-makers"
            className="bg-[#002FA7] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 hover:bg-[#001f6f] disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Target className="w-3.5 h-3.5" />}
            Find with Apollo
          </button>
          <button
            onClick={() => setAdding((v) => !v)}
            disabled={busy}
            data-testid="btn-add-contact-toggle"
            className="border border-zinc-300 text-zinc-700 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 hover:bg-zinc-100"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Add manual
          </button>
        </div>
      </div>

      {adding && (
        <div className="px-5 py-3 border-b border-zinc-200 bg-blue-50/40 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="contact-add-form">
          <input
            placeholder="Name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="border border-zinc-300 px-2 py-1.5 text-sm"
            data-testid="contact-input-name"
          />
          <input
            placeholder="Title"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            className="border border-zinc-300 px-2 py-1.5 text-sm"
            data-testid="contact-input-title"
          />
          <input
            placeholder="Phone"
            value={draft.phone}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            className="border border-zinc-300 px-2 py-1.5 text-sm"
            data-testid="contact-input-phone"
          />
          <input
            placeholder="Email"
            value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            className="border border-zinc-300 px-2 py-1.5 text-sm"
            data-testid="contact-input-email"
          />
          <button
            onClick={addManual}
            disabled={busy}
            data-testid="btn-save-contact"
            className="bg-emerald-600 text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      )}

      {contacts.length === 0 ? (
        <div className="px-5 py-10 text-center text-zinc-500 text-sm" data-testid="contacts-empty">
          No decision-makers yet. Click <strong>Find with Apollo</strong> to enrich,
          or add one manually.
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {contacts.map((c) => {
            const status = STATUS_STYLES[c.status] || STATUS_STYLES.pending;
            const isActive = c.id === activeId;
            return (
              <div
                key={c.id}
                className={`px-5 py-3 flex items-center gap-3 ${isActive ? "bg-blue-50/50" : ""}`}
                data-testid={`contact-row-${c.id}`}
              >
                <div
                  className={`px-1.5 py-1 border text-[11px] font-bold font-mono w-10 text-center ${scoreColor(c.score)}`}
                  data-testid={`contact-score-${c.id}`}
                  title="Decision-maker score (0-100)"
                >
                  {Number(c.score) || 0}
                </div>
                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div className="md:col-span-1">
                    <div className="font-semibold text-sm text-zinc-900 truncate flex items-center gap-1.5">
                      {isActive && (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Active contact" />
                      )}
                      {c.name || <span className="text-zinc-400">Unnamed</span>}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{c.title || "—"}</div>
                  </div>
                  <div className="text-xs text-zinc-700 md:col-span-1 truncate">
                    <Phone className="inline w-3 h-3 mr-1 text-zinc-400" />
                    {c.phone || <span className="text-zinc-300">no phone</span>}
                  </div>
                  <div className="text-xs text-zinc-700 md:col-span-1 truncate">
                    <Mail className="inline w-3 h-3 mr-1 text-zinc-400" />
                    {c.email || <span className="text-zinc-300">no email</span>}
                  </div>
                  <div className="md:col-span-1 flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${status.cls}`}>
                      {status.label}
                    </span>
                    {c.attempts > 0 && (
                      <span className="text-[10px] text-zinc-500">{c.attempts} attempt(s)</span>
                    )}
                    {c.source === "apollo" && (
                      <span className="text-[9px] uppercase tracking-wider text-[#002FA7] font-bold">Apollo</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!isActive && c.phone && (
                    <button
                      title="Set as active contact"
                      onClick={() => activateContact(c.id)}
                      disabled={busy}
                      data-testid={`btn-activate-${c.id}`}
                      className="p-1.5 text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                    >
                      <UserCheck className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    title="Mark as confirmed (right person)"
                    onClick={() => patchContact(c.id, { status: "confirmed" })}
                    disabled={busy}
                    data-testid={`btn-confirm-${c.id}`}
                    className="p-1.5 text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    title="Wrong person — rotate to next"
                    onClick={() => patchContact(c.id, { status: "wrong_person" })}
                    disabled={busy}
                    data-testid={`btn-wrong-${c.id}`}
                    className="p-1.5 text-zinc-500 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                  >
                    <UserX className="w-4 h-4" />
                  </button>
                  {c.linkedin && (
                    <a
                      href={c.linkedin} target="_blank" rel="noreferrer"
                      className="p-1.5 text-zinc-500 hover:text-[#0a66c2]"
                      data-testid={`btn-linkedin-${c.id}`}
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    title="Delete contact"
                    onClick={() => removeContact(c.id)}
                    disabled={busy}
                    data-testid={`btn-delete-contact-${c.id}`}
                    className="p-1.5 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
